import React, { useState, useEffect } from 'react';
import AdSense from "react-adsense";
import axios from 'axios';
import { Helmet } from "react-helmet";
import { FaMicrophone, FaStop } from 'react-icons/fa';
import { alert_error, alert_success } from "../../../AppContext.js";
import { useParams, useNavigate } from 'react-router-dom';
import { RingLoader } from "react-spinners";
import { TiTick } from 'react-icons/ti';
import { MdVolumeUp } from 'react-icons/md';
import Language_i18 from '../../../js/useNationalLanguage.jsx';
import Orders_plans from "../../../order/orders/orders.jsx";
import History from "../../../radio_signal/history.jsx";
import Options from "../options.jsx";
import OrdersHomeAi from "./orders_blog.jsx";
import OrdersHomeNotes from "./orders_notes.jsx";
import Select from 'react-select';
import classNames from "classnames";

const Orders_home = () => {
    const { slug_1 } = useParams();
    const { slug_2 } = useParams();
    const { slug_3 } = useParams();
    const { lang } = Language_i18();
    const [userOption, setUserOption] = useState("changeCard");
    const navigate = useNavigate();
    function switch_router(link) {
        navigate(link);
    };

    const seoByTool = {
        text_speech: {
            en: {
                title: "Text to Speech (TTS) — AI Voice Generator",
                description:
                    "Convert text into natural-sounding speech for narration, learning, and accessibility.",
                keywords:
                    "text to speech, TTS, AI voice, voice generator, narration, accessibility, Vietnamese TTS",
            },
            vi: {
                title: "Chuyển văn bản thành giọng nói (Text to Speech)",
                description:
                    "Công cụ chuyển văn bản thành giọng nói phục vụ thuyết minh nội dung, học tập và trợ năng.",
                keywords:
                    "chuyển văn bản thành giọng nói, text to speech, TTS, giọng nói AI, đọc văn bản, trợ năng, tiếng Việt",
            },
        },
        speech_text: {
            en: {
                title: "Speech to Text (STT) — Audio Transcription",
                description:
                    "Transcribe speech into text to speed up note-taking, reporting, and content editing.",
                keywords:
                    "speech to text, STT, transcription, audio to text, voice to text, meeting notes, Vietnamese STT",
            },
            vi: {
                title: "Chuyển giọng nói thành văn bản (Speech to Text)",
                description:
                    "Công cụ chuyển giọng nói thành văn bản giúp ghi chú nhanh, tạo báo cáo và chỉnh sửa nội dung.",
                keywords:
                    "chuyển giọng nói thành văn bản, speech to text, STT, phiên âm, ghi chú, audio to text, tiếng Việt",
            },
        },
        image_text: {
            en: {
                title: "Image to Text (OCR) — Extract Text from Images",
                description:
                    "Extract readable text from images and screenshots (OCR) to reduce manual typing and support verification.",
                keywords:
                    "image to text, OCR, extract text, screenshot to text, document OCR, receipt OCR, text recognition",
            },
            vi: {
                title: "Trích xuất chữ từ hình ảnh (OCR)",
                description:
                    "Công cụ OCR trích xuất chữ từ ảnh và ảnh chụp màn hình, hỗ trợ nhập liệu nhanh và kiểm chứng thông tin.",
                keywords:
                    "OCR, chuyển hình ảnh sang chữ, trích xuất chữ, nhận dạng ký tự, ảnh chụp màn hình, tài liệu",
            },
        },
    };

    const activeSeo = seoByTool?.[slug_2]?.[lang] || seoByTool?.[slug_2]?.en;
    const introByTool = {
        text_speech: {
            en: {
                heading: "About this AI module",
                summary:
                    "Text-to-Speech is provided as a practical utility to support content creation and accessibility inside the reward-based collaboration workflow described in the thesis.",
                points: [
                    "Create narration for task descriptions, guides, and product content.",
                    "Improve accessibility by turning long text into audio.",
                    "Designed as a lightweight add-on module that can be extended later.",
                ],
            },
            vi: {
                heading: "Giới thiệu mô-đun AI",
                summary:
                    "Chức năng Text-to-Speech được xây dựng như một tiện ích thực dụng, hỗ trợ tạo nội dung và trợ năng trong luồng cộng tác có thưởng của đồ án.",
                points: [
                    "Tạo giọng đọc cho mô tả nhiệm vụ, hướng dẫn và nội dung sản phẩm.",
                    "Hỗ trợ trợ năng bằng cách chuyển văn bản dài thành âm thanh.",
                    "Thiết kế theo dạng mô-đun nhẹ, dễ mở rộng về sau.",
                ],
            },
        },
        speech_text: {
            en: {
                heading: "About this AI module",
                summary:
                    "Speech-to-Text helps users convert voice into structured text, improving speed and usability for reporting and content workflows in the system.",
                points: [
                    "Fast note-taking and report drafting from voice input.",
                    "Useful for mobile users who prefer speaking over typing.",
                    "Built to fit the same API-driven, modular architecture.",
                ],
            },
            vi: {
                heading: "Giới thiệu mô-đun AI",
                summary:
                    "Chức năng Speech-to-Text giúp chuyển giọng nói thành văn bản có cấu trúc, tăng tốc nhập liệu và hỗ trợ các luồng báo cáo/nội dung trong hệ thống.",
                points: [
                    "Ghi chú nhanh và tạo báo cáo từ giọng nói.",
                    "Phù hợp người dùng mobile, giảm thao tác gõ phím.",
                    "Tương thích kiến trúc mô-đun và luồng API của hệ thống.",
                ],
            },
        },
        image_text: {
            en: {
                heading: "About this AI module",
                summary:
                    "Image-to-Text (OCR) extracts text from screenshots and documents to reduce manual typing and support proof/verification steps in the workflow.",
                points: [
                    "Extract text from screenshots, receipts, and documents.",
                    "Support verification by converting visual information into searchable text.",
                    "Keeps the system modular: OCR is an independent utility service.",
                ],
            },
            vi: {
                heading: "Giới thiệu mô-đun AI",
                summary:
                    "Chức năng OCR trích xuất chữ từ ảnh/chụp màn hình giúp giảm nhập liệu thủ công và hỗ trợ bước kiểm chứng minh chứng trong hệ thống.",
                points: [
                    "Trích xuất chữ từ ảnh chụp màn hình, biên lai, tài liệu.",
                    "Hỗ trợ kiểm chứng bằng cách chuyển thông tin thị giác thành văn bản tra cứu được.",
                    "Tách rời theo mô-đun: OCR hoạt động như một tiện ích độc lập.",
                ],
            },
        },
    };
    const activeIntro = introByTool?.[slug_2]?.[lang] || introByTool?.[slug_2]?.en;
    return (
        <>
            {slug_2 === "home_ai" && (
                <OrdersHomeAi />
            )}
            {slug_2 === "home_notes" && (
                <OrdersHomeNotes />
            )}
            {slug_2 === "home_vip" && (
                <Options category="ai_home_vip" />
            )}
            {slug_1 === "plans" && (
                <Orders_plans />
            )}
            {slug_2 === "text_speech" && slug_3 === "history" && (
                <History />
            )}
            {(slug_1 === "plans" || slug_1 === "orders_once") &&
                (slug_2 === "text_speech" || slug_2 === "speech_text" || slug_2 === "image_text") &&
                activeSeo && (
                <>
                    <Helmet>
                        <title>{activeSeo.title}</title>
                        <meta name="description" content={activeSeo.description} />
                        <meta name="keywords" content={activeSeo.keywords} />

                        <meta property="og:title" content={activeSeo.title} />
                        <meta property="og:type" content="website" />
                        <meta property="og:image" content="https://hust.media/reactapp/hustmedia.ico" />
                        <meta property="og:description" content={activeSeo.description} />
                        <meta property="og:url" content="https://hust.media" />
                        <link rel="icon" href="https://hust.media/reactapp/hustmedia.ico" />
                    </Helmet>

                    <div className="max-w-5xl mx-auto">
                        <div className="mt-3 mx-1 grid grid-cols-1">
                            <div className="mb-2 bg-green-200/60 border border-white/60 border-l-4 border-l-emerald-500 py-2.5 px-3 rounded-2xl shadow-sm backdrop-blur-md md:col-span-2">
                                <div className="flex items-start gap-2">
                                    <div className="mt-1.5 h-2 w-2 rounded-full bg-emerald-500/90 shadow-sm" />
                                    <div className="text-slate-900 font-semibold leading-snug">
                                        {activeIntro?.heading}
                                    </div>
                                </div>
                                <div className="text-sm text-slate-700 mt-0.5 leading-snug">
                                    {activeIntro?.summary}
                                </div>
                                <ul className="list-disc pl-5 mt-1 text-sm text-slate-800 space-y-0.5 marker:text-emerald-600">
                                    {(activeIntro?.points || []).map((p, i) => (
                                        <li key={i}>{p}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}


        </>
    );
};

export default Orders_home;
